import React from "react";
import Modal from "react-modal";
import { updateAttributesOnDocument } from "util/pouchActions";
import { Lifter, LifterDivision, Meet } from "types";
import Select from "components/select/Select";
import {
  getAwardDivisionOptions,
  getBarAndCollarsWeightOptions,
  getFlightOptions,
  getPlatformOptions,
  getSessionOptions,
} from "util/options";
import size from "lodash/size";
import { getDivisionDoc } from "util/lifterHelper";
import first from "lodash/first";

const initialSelections: {
  platformId: string | null;
  session: number | null;
  flight: string | null;
  divisionId: string | null;
  squatBarAndCollarsWeight: string | null;
  benchBarAndCollarsWeight: string | null;
  deadliftBarAndCollarsWeight: string | null;
} = {
  platformId: null,
  session: null,
  flight: null,
  divisionId: null,
  squatBarAndCollarsWeight: null,
  benchBarAndCollarsWeight: null,
  deadliftBarAndCollarsWeight: null,
};

const BulkEditModal = ({
  meet,
  onRequestClose,
  isOpen,
  filteredLifters,
  activeFilters,
  filters,
}: {
  meet: Meet;
  onRequestClose: () => void;
  isOpen: boolean;
  filteredLifters: Lifter[];
  activeFilters: boolean;
  filters: {
    gender: string | null;
    rawOrEquipped: string | null;
  };
}) => {
  const [selections, setSelections] = React.useState(initialSelections);
  const [loading, setLoading] = React.useState(false);

  const close = () => {
    setSelections(initialSelections);
    setLoading(false);
    onRequestClose();
  };

  const updateLiters = () => {
    setLoading(true);

    if (
      selections.divisionId ||
      selections.flight ||
      selections.platformId ||
      selections.session ||
      selections.squatBarAndCollarsWeight ||
      selections.benchBarAndCollarsWeight ||
      selections.deadliftBarAndCollarsWeight
    ) {
      filteredLifters.forEach((lifter) => {
        const updatedData: Partial<Lifter> = {};
        if (selections.platformId) {
          updatedData.platformId = selections.platformId;
        }
        if (selections.session) {
          updatedData.session = selections.session;
        }
        if (selections.flight) {
          updatedData.flight = selections.flight;
        }
        if (selections.divisionId) {
          const hasExisting = lifter.divisions.some(
            (d) => d.divisionId === selections.divisionId,
          );
          if (!hasExisting) {
            const divisionDoc = getDivisionDoc(selections.divisionId, meet);
            if (divisionDoc) {
              const lifterDivision: LifterDivision = {
                divisionId: selections.divisionId,
                rawOrEquipped: filters.rawOrEquipped,
                declaredAwardsWeightClassId: first(
                  Object.keys(divisionDoc?.weightClasses),
                ),
              };
              updatedData.divisions = [...lifter.divisions, lifterDivision];
            }
          }
        }

        if (selections.squatBarAndCollarsWeight) {
          updatedData.squatBarAndCollarsWeight =
            selections.squatBarAndCollarsWeight === "AUTO"
              ? ""
              : selections.squatBarAndCollarsWeight;
        }
        if (selections.benchBarAndCollarsWeight) {
          updatedData.benchBarAndCollarsWeight =
            selections.benchBarAndCollarsWeight === "AUTO"
              ? ""
              : selections.benchBarAndCollarsWeight;
        }
        if (selections.deadliftBarAndCollarsWeight) {
          updatedData.deadliftBarAndCollarsWeight =
            selections.deadliftBarAndCollarsWeight === "AUTO"
              ? ""
              : selections.deadliftBarAndCollarsWeight;
        }

        if (size(updatedData)) {
          updateAttributesOnDocument(meet._id, lifter._id, updatedData);
        }
      });
      // give time for data to loop back into the UI from pouchdb
      // usually we use a full page loader but that would wipe out the filters.
      setTimeout(() => close(), size(filteredLifters) * 150);
    } else {
      close();
    }
  };

  const onChangeSelectFilter = (
    name: keyof typeof initialSelections,
    value: string | number | undefined | null,
  ) => {
    setSelections({ ...selections, [name]: value });
  };

  const platformOptions = getPlatformOptions({}, meet);

  const barAndCollarsOptions = React.useMemo(() => {
    const autoOption: { value: string | number | null; label: string }[] = [
      { label: "Auto", value: "AUTO" },
    ];

    return autoOption.concat(getBarAndCollarsWeightOptions({}, meet));
  }, [meet]);

  return (
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      contentLabel="Bulk Edit Lifters"
      onRequestClose={onRequestClose}
      className={{
        base: "bulk-edit-modal",
        afterOpen: "bulk-edit-modal-after-open",
        beforeClose: "bulk-edit-modal-before-close",
      }}
      overlayClassName={{
        base: "modal-overlay",
        afterOpen: "",
        beforeClose: "",
      }}
    >
      <div className="content">
        <h2>
          Bulk Edit {filteredLifters.length}{" "}
          {activeFilters ? "Filtered" : "(ALL)"} Lifters
        </h2>

        <label className="bulk-edit-row">
          Platform:
          <Select
            name="platformId"
            value={selections.platformId}
            options={platformOptions}
            blankValueLabel="NO CHANGE"
            onChange={(value) => onChangeSelectFilter("platformId", value)}
          />
        </label>
        <label className="bulk-edit-row">
          Session:
          <Select
            name="session"
            value={selections.session}
            options={getSessionOptions()}
            blankValueLabel="NO CHANGE"
            onChange={(value) => onChangeSelectFilter("session", value)}
          />
        </label>
        <label className="bulk-edit-row">
          Flight:
          <Select
            name="flight"
            value={selections.flight}
            options={getFlightOptions()}
            blankValueLabel="NO CHANGE"
            onChange={(value) => onChangeSelectFilter("flight", value)}
          />
        </label>

        <label className="bulk-edit-row">
          Add Division:
          {!filters.gender || !filters.rawOrEquipped ? (
            <div>Must filter by Gender and Equipment Level</div>
          ) : (
            <Select
              disabled={!filters.gender || !filters.rawOrEquipped}
              name="divisionId"
              value={selections.divisionId}
              options={getAwardDivisionOptions(
                {
                  _id: "fake",
                  gender: filters.gender,
                  divisions: [{ rawOrEquipped: filters.rawOrEquipped }],
                },
                meet,
                0,
              )}
              blankValueLabel="NO CHANGE"
              onChange={(value) => onChangeSelectFilter("divisionId", value)}
            />
          )}
        </label>

        <label className="bulk-edit-row">
          Squat Bar And Collars:
          <Select
            name="squatBarAndCollarsWeight"
            value={selections.squatBarAndCollarsWeight}
            options={barAndCollarsOptions}
            blankValueLabel="NO CHANGE"
            onChange={(value) =>
              onChangeSelectFilter("squatBarAndCollarsWeight", value)
            }
          />
        </label>
        <label className="bulk-edit-row">
          Bench Bar And Collars:
          <Select
            name="benchBarAndCollarsWeight"
            value={selections.benchBarAndCollarsWeight}
            options={barAndCollarsOptions}
            blankValueLabel="NO CHANGE"
            onChange={(value) =>
              onChangeSelectFilter("benchBarAndCollarsWeight", value)
            }
          />
        </label>
        <label className="bulk-edit-row">
          Deadlift Bar And Collars:
          <Select
            name="deadliftBarAndCollarsWeight"
            value={selections.deadliftBarAndCollarsWeight}
            options={barAndCollarsOptions}
            blankValueLabel="NO CHANGE"
            onChange={(value) =>
              onChangeSelectFilter("deadliftBarAndCollarsWeight", value)
            }
          />
        </label>

        <p>
          WARNING: THIS WILL OVERWRITE DATA FOR{" "}
          {activeFilters ? "THE CURRENTLY FILTERED LIFTERS" : "ALL LIFTERS"}.{" "}
          {filteredLifters.length} WILL BE UPDATED. THIS CANNOT BE UNDONE.
        </p>
      </div>
      <div className="button-row">
        <button disabled={loading} onClick={close} style={{ marginRight: 12 }}>
          Cancel
        </button>
        <button disabled={loading} onClick={updateLiters}>
          {loading ? "Saving..." : "Save"}
        </button>
      </div>
    </Modal>
  );
};

export default BulkEditModal;
